import moment from 'moment-timezone'

const defaultFilterLabels = Object.freeze({
  start: 'Start Date',
  end: 'End Date',
  start_date: 'Start Date',
  end_date: 'End Date',
  date: 'Date',
  visitDate: 'Visit Date',
  name: 'Name',
  associates: 'Associate',
  associate_ids: 'Associate',
  associate_number: 'Associate',
  departments: 'Department',
  department_ids: 'Department',
  roles: 'Position',
  role_ids: 'Position',
  statuses: 'Status',
  status: 'Status',
  shiftStatus: 'Shift Status',
  shiftPaymentStatus: 'Payment Status',
  types: 'Type',
  role: 'Position',
  legalEntity: 'Legal Entity',
  shift_statuses: 'Shift Status',
  shift_payment_statuses: 'Payment Status',
  payment_types: 'Payment Type',
  pay_codes: 'Pay Code',
  auth_types: 'Violation',
  auth_statuses: 'Violation Status',
  legal_entities: 'Legal Entity',
  facility_project_ids: 'Client',
  request_types: 'Request Type',
  manually_created: 'Manually Created',
  adjusted: 'Adjusted',
  zero_rate: 'Zero Rate',
  orientation_period: 'Orientation Period',
  training_period: 'Training Period',
  assigned_to_me: 'Assigned to Me',
  hours_from: 'Min Hours',
  hours_to: 'Max Hours',
  job_title_codes: 'Job Title Code',
  pay_type_codes: 'Pay Type Code'
})

const defaultTextFilterKeys = Object.freeze([
  'name',
  'search',
  'query',
  'email',
  'phone',
  'associate_number',
  'visitor',
  'vendor_name',
  'destination',
  'reason',
  'title',
  'description'
])

const numericFilterKeyPattern = /(amount|count|duration|hours|max|min|rate|score|total|value|_from|_to)$/i

export default {
  data() {
    return {
      pagination: {
        pageSizeOptions: ['10', '50', '100', '200', '500'],
        pageSize: 50,
        current: 1,
        total: 0,
        showSizeChanger: true,
        showTotal: total => `Total: ${total}`
      },

      tableScrollHeight: 300,
      tableKey: undefined
    }
  },
  computed: {
    tableFilterStorage() {
      const filters = this.tableFilters ?? this.filters ?? this.filterNew ?? {}
      const hasLocalFilters =
        Object.prototype.hasOwnProperty.call(this.$data ?? {}, 'filters') ||
        Object.prototype.hasOwnProperty.call(this.$data ?? {}, 'filterNew')

      return {
        type: hasLocalFilters ? 'local' : 'route',
        filters
      }
    },

    tableFiltersSource() {
      return this.tableFilterStorage?.filters ?? {}
    },

    activeFilterTags() {
      return this.getActiveFilterTags(this.tableFiltersSource)
    },

    countFilters() {
      return this.activeFilterTags.length
    },

    canResetFilters() {
      const currentFilters = this.getComparableCurrentFilterEntries()
      const resetFilters = this.getComparableResetFilterEntries()

      return JSON.stringify(currentFilters) !== JSON.stringify(resetFilters)
    }
  },
  methods: {
    calculateTableHeight() {
      this.$nextTick(() => {
        const containerRef = this.$refs.tableContainer
        const container = containerRef?.$el ?? containerRef ?? this.$el?.querySelector?.('.table-container')
        if (!container) return

        const rect = container.getBoundingClientRect()
        const windowHeight = window.innerHeight
        const offsetTop = rect.top

        const availableHeight = windowHeight - offsetTop

        const paddingAndFooter = 120
        const scrollHeight = availableHeight - paddingAndFooter

        this.tableScrollHeight = scrollHeight > 200 ? scrollHeight : 200
      })
    },
    resetFilters() {
      const storage = this.tableFilterStorage ?? {}
      const resetFilters = this.getTableResetFilters()

      if (storage.type === 'local' && Object.prototype.hasOwnProperty.call(this.$data ?? {}, 'filters')) {
        this.filters = resetFilters
        return
      } else if (storage.type === 'local' && Object.prototype.hasOwnProperty.call(this.$data ?? {}, 'filterNew')) {
        this.filterNew = resetFilters
        return
      } else if (storage.type === 'local' && this.filterNew !== undefined) {
        this.$emit('update:filterNew', resetFilters)
        this.$emit('update:currentFilters', resetFilters)
        return
      }

      const resetQuery = this.getTableResetQuery()

      if (Object.keys(this.$route?.query ?? {}).length || Object.keys(resetQuery).length) {
        this.$router.replace({ query: resetQuery }).catch(() => {})
      }
    },
    getTableResetFilters() {
      const resetFilters = this.tableResetFilters ?? {}
      const value = typeof resetFilters === 'function' ? resetFilters.call(this) : resetFilters

      return this.cloneTableFilterValue(value ?? {})
    },
    getTableResetQuery() {
      const resetQuery = this.tableResetQuery ?? {}
      const value = typeof resetQuery === 'function' ? resetQuery.call(this) : resetQuery

      return this.cloneTableFilterValue(value ?? {})
    },
    cloneTableFilterValue(value) {
      if (Array.isArray(value)) {
        return value.map(item => this.cloneTableFilterValue(item))
      }

      if (value && typeof value === 'object' && !moment.isMoment(value)) {
        return Object.entries(value).reduce((result, [key, item]) => {
          result[key] = this.cloneTableFilterValue(item)

          return result
        }, {})
      }

      return value
    },
    getActiveFilterTags(filters = {}) {
      const hiddenFilters = this.tableHiddenFilters ?? []
      const defaultFilters = this.tableDefaultFilters ?? {}
      const filterMeta = this.tableFilterMeta ?? {}
      const filterOrder = this.tableFilterOrder ?? []
      const filterEntries = {
        ...this.getVisiblePredefinedFilterEntries(filterMeta),
        ...filters
      }

      const activeFilters = Object.entries(filterEntries).filter(([key, value]) => {
        const metaData = filterMeta[key] ?? {}

        if (hiddenFilters.includes(key))
          return false

        if (this.isEmptyFilterValue(value))
          return false

        if (!metaData.predefined && this.isDefaultFilterValue(key, value, defaultFilters))
          return false

        return true
      })

      const activeFilterMap = new Map(activeFilters)
      const ordered = []

      filterOrder.forEach(key => {
        if (activeFilterMap.has(key)) {
          ordered.push([key, activeFilterMap.get(key)])
          activeFilterMap.delete(key)
        }
      })

      return [...ordered, ...activeFilterMap].map(([key, value]) => ({
          key,
          label: this.getFilterLabel(key),
          value: this.getFilterValueLabel(key, value),
          rawValue: value,
          closable: this.isFilterClosable(key, filterMeta)
        }))
    },
    getComparableCurrentFilterEntries() {
      const filterMeta = this.tableFilterMeta ?? {}
      const filterEntries = {
        ...this.getVisiblePredefinedFilterEntries(filterMeta),
        ...this.tableFiltersSource
      }

      return this.normalizeComparableFilterEntries(filterEntries)
    },
    getComparableResetFilterEntries() {
      const filterMeta = this.tableFilterMeta ?? {}
      const storage = this.tableFilterStorage ?? {}
      const resetFilters = storage.type === 'local' ? this.getTableResetFilters() : this.getTableResetQuery()
      const filterEntries = {
        ...this.getPredefinedFilterEntries(filterMeta),
        ...resetFilters
      }

      return this.normalizeComparableFilterEntries(filterEntries)
    },
    normalizeComparableFilterEntries(filters = {}) {
      return Object.entries(filters)
        .filter(([, value]) => !this.isEmptyFilterValue(value))
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .reduce((result, [key, value]) => {
          result[key] = this.normalizeComparableFilterValue(value)

          return result
        }, {})
    },
    normalizeComparableFilterValue(value) {
      if (Array.isArray(value)) {
        return value.map(item => this.normalizeComparableFilterValue(item)).sort()
      }

      if (moment.isMoment(value)) {
        return value.format('YYYY-MM-DD')
      }

      if (value && typeof value === 'object') {
        return this.normalizeComparableFilterEntries(value)
      }

      return String(value)
    },
    getVisiblePredefinedFilterEntries(filterMeta = {}) {
      return this.getPredefinedFilterEntries(filterMeta, { includeCleared: false })
    },
    getPredefinedFilterEntries(filterMeta = {}, options = {}) {
      const includeCleared = options.includeCleared ?? true

      return Object.entries(filterMeta).reduce((result, [key, meta]) => {
        if (!meta?.predefined) return result
        if (meta.show === false) return result
        if (!includeCleared && this.isPredefinedFilterCleared(key, meta)) return result
        if (!Object.prototype.hasOwnProperty.call(meta, 'value')) return result

        result[key] = typeof meta.value === 'function' ? meta.value.call(this) : meta.value

        return result
      }, {})
    },
    isFilterClosable(key, filterMeta = {}) {
      const meta = filterMeta[key] ?? {}

      return meta.closable ?? meta.removable ?? true
    },
    isPredefinedFilterCleared(key, meta = {}) {
      const storage = this.tableFilterStorage ?? {}
      const filters = storage.filters ?? {}
      const clearValue = this.getFilterClearValue(key, meta)

      if (storage.type === 'route') {
        const query = this.$route?.query ?? {}

        return (
          Object.prototype.hasOwnProperty.call(query, key) &&
          JSON.stringify(query[key]) === JSON.stringify(clearValue)
        )
      }

      return (
        Object.prototype.hasOwnProperty.call(filters, key) &&
        this.isEmptyFilterValue(filters[key]) &&
        !this.isEmptyFilterValue(clearValue)
      )
    },
    isEmptyFilterValue(value) {
      if (Array.isArray(value)) {
        return value.length === 0 || value.every(item => this.isEmptyFilterValue(item))
      }

      return value === undefined || value === null || value === ''
    },
    isDefaultFilterValue(key, value, defaultFilters = {}) {
      if (!Object.prototype.hasOwnProperty.call(defaultFilters, key)) {
        return false
      }

      return JSON.stringify(value) === JSON.stringify(defaultFilters[key])
    },
    getFilterLabel(key) {
      const labels = {
        ...defaultFilterLabels,
        ...(this.tableFilterLabels ?? {})
      }

      return labels[key] ?? this.humanizeFilterKey(key)
    },
    humanizeFilterKey(key) {
      return String(key)
        .replace(/_ids$/, '')
        .replace(/_id$/, '')
        .replace(/_/g, ' ')
        .replace(/\w\S*/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    },
    getFilterValueLabel(key, value) {
      if (Array.isArray(value)) {
        const activeValues = value.filter(item => !this.isEmptyFilterValue(item))

        if (this.isDateRangeFilterValue(key, activeValues)) {
          return activeValues.map(item => this.formatFilterDate(item)).join(' - ')
        }

        return `${activeValues.length} selected`
      }

      return this.getSingleFilterValueLabel(value, key)
    },
    getSingleFilterValueLabel(value, key) {
      const valueLabels = this.tableFilterValueLabels?.[key]
      if (valueLabels && Object.prototype.hasOwnProperty.call(valueLabels, String(value))) {
        return valueLabels[String(value)]
      }

      if (value === true || value === 'true') {
        return 'Yes'
      }

      if (value === false || value === 'false') {
        return 'No'
      }

      if (moment.isMoment(value)) {
        return this.formatFilterDate(value)
      }

      if (this.isDateFilterValue(key, value)) {
        return this.formatFilterDate(value)
      }

      if (this.isReadableTextFilter(key, value) || this.isReadableNumericFilter(key, value)) {
        return String(value)
      }

      if (typeof value === 'object') {
        return 'Selected'
      }

      return 'Selected'
    },
    isReadableTextFilter(key, value) {
      if (typeof value !== 'string') {
        return false
      }

      const textFilterKeys = this.tableTextFilterKeys ?? defaultTextFilterKeys
      const normalizedKey = String(key ?? '').toLowerCase()

      return textFilterKeys.some(item => normalizedKey === String(item).toLowerCase())
    },
    isReadableNumericFilter(key, value) {
      if (typeof value !== 'number') {
        return false
      }

      return numericFilterKeyPattern.test(String(key ?? ''))
    },
    isDateFilterValue(key, value) {
      if (!value || typeof value !== 'string') {
        return false
      }

      const normalizedKey = String(key ?? '').toLowerCase()
      const isDateKey = ['date', 'start', 'end'].some(part => normalizedKey.includes(part))

      return isDateKey && moment(value, 'YYYY-MM-DD', true).isValid()
    },
    isDateRangeFilterValue(key, value) {
      if (!Array.isArray(value) || value.length !== 2) {
        return false
      }

      const normalizedKey = String(key ?? '').toLowerCase()
      const isDateKey = ['date', 'start', 'end'].some(part => normalizedKey.includes(part))

      return isDateKey && value.every(item => moment.isMoment(item) || this.isDateFilterValue(key, item))
    },
    formatFilterDate(value) {
      return moment(value).format('MM/DD/YYYY')
    },
    removeFilter(filter) {
      const key = typeof filter === 'string' ? filter : filter?.key
      if (!key) return
      if (filter?.closable === false) return

      const filterMeta = this.tableFilterMeta ?? {}
      const meta = filterMeta[key] ?? {}
      if (this.isFilterClosable(key, filterMeta) === false) return

      const emptyValue = this.getFilterClearValue(key, meta)
      const storage = this.tableFilterStorage ?? {}

      if (storage.type === 'local' && Object.prototype.hasOwnProperty.call(this.$data ?? {}, 'filters')) {
        this.filters = {
          ...this.filters,
          [key]: emptyValue
        }
        return
      }

      if (storage.type === 'local' && Object.prototype.hasOwnProperty.call(this.$data ?? {}, 'filterNew')) {
        this.filterNew = {
          ...this.filterNew,
          [key]: emptyValue
        }
        return
      }

      if (storage.type === 'local' && this.filterNew !== undefined) {
        const nextFilters = {
          ...this.filterNew,
          [key]: emptyValue
        }

        this.$emit('update:filterNew', nextFilters)
        this.$emit('update:currentFilters', nextFilters)
      }

      if (storage.type !== 'local') {
        const query = { ...this.$route.query }
        if (emptyValue === undefined) {
          delete query[key]
        } else {
          query[key] = emptyValue
        }
        this.$router.replace({ query }).catch(() => {})
      }
    },
    getFilterClearValue(key, meta = {}) {
      if (Object.prototype.hasOwnProperty.call(meta, 'clearValue')) {
        return typeof meta.clearValue === 'function' ? meta.clearValue.call(this) : meta.clearValue
      }

      const emptyValues = this.tableFilterEmptyValues ?? {}
      if (Object.prototype.hasOwnProperty.call(emptyValues, key)) {
        return emptyValues[key]
      }

      return undefined
    },
    openFilterEditor() {
      if (typeof this.openFiltersModal === 'function') {
        this.openFiltersModal()
        return
      }

      const visibleField = [
        'visibleFiltersModal',
        'filtersModalVisible',
        'visiblePaymentsFilters',
        'visibleProfilesFilters',
        'visibleFilters',
        'visibleSequenceFilters',
        'visibleAttachedFilters',
        'visitorFiltersVisible'
      ].find(field => Object.prototype.hasOwnProperty.call(this.$data ?? {}, field))

      if (visibleField) {
        this[visibleField] = true
      }
    },
    getRowClassName(_, index) {
      return index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
    },

    reorderColumns() {
      if (!this.canMutateTableColumns() || !Array.isArray(this.columns)) return

      const fixedColumns = (this.columns ?? []).filter(col => col && col.fixed === 'left')

      const nonFixedColumns = (this.columns ?? [])
        .filter(col => col && col.fixed !== 'left')
        .sort(
          (a, b) =>
            Number(a?.originalIndex ?? Number.MAX_SAFE_INTEGER) - Number(b?.originalIndex ?? Number.MAX_SAFE_INTEGER)
        )

      this.columns = [...fixedColumns, ...nonFixedColumns]
    },

    toggleFixed(column) {
      if (!this.canMutateTableColumns()) return
      if (!column || !column.key) return

      const col = (this.columns ?? []).find(c => c && c.key === column.key)
      if (!col) return

      const isFixed = col.fixed === 'left'

      this.$set(col, 'fixed', isFixed ? null : 'left')
      this.reorderColumns()
      this.saveFixedColumns()
    },
    saveFixedColumns() {
      if (!Array.isArray(this.columns)) return

      const fixedKeys = (this.columns ?? [])
        .filter(col => col && col.fixed === 'left' && col.key !== undefined)
        .map(col => col.key)

      const pinTable = JSON.parse(localStorage.getItem('pinTable') || '{}')

      pinTable[this.tableKey ?? this.$route.path] = fixedKeys

      localStorage.setItem('pinTable', JSON.stringify(pinTable))
    },
    createTitleRender(column) {
      if (!column) return undefined

      const h = this.$createElement

      return h(
        'span',
        {
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer'
          },
          class: {
            'title-pin': true
          },
          on: {
            click: e => {
              e.preventDefault()
              e.stopPropagation()
              this.toggleFixed(column)
            }
          }
        },
        [
          h('span', column.title),
          h('a-icon', {
            attrs: {
              icon: 'carbon:pin-filled'
            },
            style: {
              color: '#999',
              fontSize: '12px',
              marginLeft: '4px'
            }
          })
        ]
      )
    },
    loadFixedColumns() {
      if (!this.canMutateTableColumns() || !Array.isArray(this.columns)) return

      const pinTable = JSON.parse(localStorage.getItem('pinTable') || '{}')
      const fixedKeys = pinTable[this.tableKey ?? this.$route.path] || []

      this.columns = (this.columns ?? []).filter(Boolean).map(col => {
        const isFixed = fixedKeys.includes(col.key)
        col.fixed = isFixed ? 'left' : null

        return {
          ...col,
          title: !!col.originalIndex || col.originalIndex === 0 ? this.createTitleRender(col) : col.title
        }
      })

      this.columns = [...this.columns]
    },
    canMutateTableColumns() {
      const columnsOption = this.$options.computed?.columns

      return !columnsOption || (typeof columnsOption === 'object' && typeof columnsOption.set === 'function')
    }
  },
  mounted() {
    this.calculateTableHeight()
    this.loadFixedColumns()
    this.reorderColumns()
    window.addEventListener('resize', this.calculateTableHeight)
  },
  destroyed() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  beforeDestroy() {
    this.saveFixedColumns()
  }
}
